import { Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import TextInputField from "../../FormFields/TextInputField/TextInputField";
import { AnnualReportFields } from "./AnnualReportFields";
import { EinTaxFields } from "./EinTaxFields";
import { LegalNoticeAdvertisementFields } from "./LegalNoticeAdvertisementFields";
import { generatePurchaseFormInitialValues, generatePurchaseValidationSchema, isAnnualSelected } from "./PurchaseFormHelpers";
import { FictitiousNameRenewalFields } from "./FictitiousNameRenewalFields";
import { promoCodes } from "../../../Redux/Shopping/shoppingHelpers";
import { BOIReportFields } from "./BOIReport";
import { PastDueAnnualReportFields } from "./PastDueAnnualReportFields";
import TermsAndConditionsFields from "./TermsAndConditionsFields";
import { GeneralPurchaseFields } from "./GeneralPurchaseFields";
import SignatureField from "./SignatureField";

export default function PurchaseForm(props: {
  onSubmit: any;
  selectedProducts: any;
  disableForm: boolean;
  initialFormOptions?: any;
  applyPromoCode: any;
  prepaid?: boolean;
  discountId?: string;
  discountAmount?: number;
  currentStep: number;
  setCurrentStep: any;
  scrollToTop: any;
  shippingMethod: string;
}) {
  const {
    onSubmit,
    selectedProducts,
    disableForm,
    initialFormOptions = {},
    applyPromoCode,
    discountId = '',
    discountAmount,
    currentStep,
    setCurrentStep,
    scrollToTop,
    shippingMethod,
  } = props;

  const [validDiscount, setValidDiscount] = useState(true);

  const annualSelected = isAnnualSelected(selectedProducts);
  const pastDueAnnualSelected = selectedProducts['pastDueAnnualReport']

  let isGeneralPurchase = true;
  if (
    annualSelected ||
    pastDueAnnualSelected ||
    selectedProducts["einTaxId"] ||
    selectedProducts["legalNoticeAdvertisement"] ||
    selectedProducts["fictitiousNameRenewal"] ||
    selectedProducts["boiReport"]
  ) {
    isGeneralPurchase = false;
  }

  let totalSteps = 1;

  const stepProducts: string[] = []

  if (!isGeneralPurchase) {
    if (annualSelected) stepProducts.push("annualReport");
    if (pastDueAnnualSelected) stepProducts.push("pastDueAnnualReport");
    if (selectedProducts["einTaxId"]) stepProducts.push("einTaxId");
    if (selectedProducts["legalNoticeAdvertisement"] && !selectedProducts["fictitiousNameRenewal"]) stepProducts.push("legalNoticeAdvertisement");
    if (selectedProducts["fictitiousNameRenewal"]) stepProducts.push("fictitiousNameRenewal");
    if (selectedProducts["boiReport"]) stepProducts.push("boiReport");

    totalSteps = stepProducts.length;
  }

  const isMultiStep = totalSteps > 1;
  const isLastStep = currentStep >= totalSteps;

  const showContactFieldsAndRef = currentStep === 1;
  const showTermsAndSignature = isLastStep;

  let nextStepText = "Next Step"

  let currentSelectedProducts = { ...selectedProducts };
  let currentProductKey = stepProducts && stepProducts.length > 0 ? stepProducts[currentStep - 1] : "";

  if (isMultiStep && !isLastStep) {
    currentSelectedProducts = {} as any;
    currentSelectedProducts[currentProductKey] = true;

    const nextProductKey = stepProducts[currentStep];
    if (nextProductKey === "einTaxId") {
      nextStepText = "EIN";
    }
  }


  // const validationSchemaFunction = useMemo(() => {
  //   const validationOptions = {
  //     validateTerms: showTermsAndSignature,
  //   }
  //   const vFn = generatePurchaseValidationSchema(currentSelectedProducts, validationOptions);
  //   return vFn;
  // }, [selectedProducts, currentStep]);


  const validationSchemaFunction = generatePurchaseValidationSchema(currentSelectedProducts, {
    validateTerms: showTermsAndSignature,
  });

  const formikRef: any = useRef(null);
  useEffect(() => {
    if (formikRef && formikRef.current && formikRef.current.validateForm) {
      formikRef.current.validateForm();
    }
  }, [selectedProducts]);

  useEffect(() => {
    // check for .purchase-form-container on page and then get parent element with class .purchase-step
    // if element is present and does not have display:none then run the rest of the code

    const purchaseFormContainer = document.querySelector(".purchase-form-container");
    const purchaseStep = purchaseFormContainer?.closest(".purchase-step");
    if (purchaseStep && getComputedStyle(purchaseStep).display === "none") {
      return;
    }

    scrollToTop();
    // focus and unfocus first input to trigger a validation
    if (formikRef && formikRef.current) {
      const firstInput: any = document.querySelector("input, select, textarea");
      if (firstInput) {
        firstInput.focus();
        firstInput.blur();
      }
    }
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep]
  );

  const scrollToError = () => {
    const silentErrors = document.querySelectorAll(".silent-error");
    if (silentErrors && silentErrors.length > 0) {
      silentErrors.forEach((x: any) => {
        if (x.focus && x.blur) {
          x.focus();
          x.blur();
        }
      });
    }

    const el: any = document.querySelector(".error");
    if (el) {
      (el?.parentElement ?? el)?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    const sel: any = document.querySelector(".silent-error");
    if (sel) {
      if (sel.focus && sel.blur) {
        sel.focus();
        sel.blur();
        sel.focus();
      }
      (sel?.parentElement ?? sel)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePromoCode = (promoCodeValue: string) => {
    if (promoCodeValue && promoCodes[promoCodeValue]) {
      setValidDiscount(true)
      applyPromoCode(promoCodeValue)
      return
    }
    setValidDiscount(false)
  }

  return (
    <>
      <div className="form-pre-information">
        {selectedProducts["einTaxId"] && currentProductKey === "einTaxId" && (
          <div className="enter-information-correctly">
            <u>Your information must match exactly</u> as on file with
            the IRS and SSA. If this information submitted here does not
            match the IRS records, this{" "}
            <u>
              <b>will cause delays</b>
            </u>{" "}
            in the processing of your EIN Application.
          </div>
        )}

        {currentStep === 1 && (
          <>
            {shippingMethod !== "physical" ? (
              <div className="ordering-message">
                YOU ARE ORDERING THIS DOCUMENT{" "}
                <u>ELECTRONICALLY VIA EMAIL</u> <br />
                <span className="red">
                  PLEASE MAKE SURE YOU ENTER YOUR EMAIL ADDRESS CORRECTLY.
                </span>{" "}
                <br />
                <u>CHECK YOUR SPAM</u> FOLDER PERIODICALLY.
              </div>
            ) : (
              <div className="ordering-message">
                YOU ARE ORDERING THIS DOCUMENT <u>USPS DELIVERY</u> <br />
              </div>
            )}
          </>
        )}
      </div>
      <div className="purchase-form-container">
        <Formik
          innerRef={formikRef}
          initialValues={generatePurchaseFormInitialValues(
            selectedProducts,
            initialFormOptions
          )}
          validationSchema={validationSchemaFunction}
          onSubmit={async (values, { setSubmitting }) => {
            // console.log("values",values);

            setSubmitting(false);
            onSubmit(values);
          }}
        >
          {({
            isSubmitting,
            values,
            errors,
            touched,
            setFieldValue,
            // setValues,
          }) => (
            <div className="purchase-form">
              <Form>

                {/* {console.log("ERRROR", errors)} */}
                {isGeneralPurchase && (
                  <>
                    <GeneralPurchaseFields
                      values={values}
                      touched={touched}
                      errors={errors}
                      setFieldValue={setFieldValue}
                    />
                  </>
                )}

                {/* Handle Annual Report */}
                {annualSelected && (
                  !currentProductKey || currentProductKey === "annualReport"
                ) && (
                    <AnnualReportFields
                      values={values}
                      touched={touched}
                      errors={errors}
                      selectedProducts={selectedProducts}
                      setFieldValue={setFieldValue}
                      showTerms={!isMultiStep}
                    />
                  )}

                {pastDueAnnualSelected && (
                  <PastDueAnnualReportFields
                    values={values}
                    touched={touched}
                    errors={errors}
                    selectedProducts={selectedProducts}
                    setFieldValue={setFieldValue}
                  />
                )}

                {selectedProducts["einTaxId"] && (
                  !currentProductKey || currentProductKey === "einTaxId"
                ) && (
                    <EinTaxFields
                      values={values}
                      touched={touched}
                      errors={errors}
                      setFieldValue={setFieldValue}
                      selectedProducts={selectedProducts}
                      showContactInfoFields={showContactFieldsAndRef}
                      showTerms={!isMultiStep}
                    />
                  )}

                {selectedProducts["legalNoticeAdvertisement"] && !selectedProducts["fictitiousNameRenewal"] && (
                  <LegalNoticeAdvertisementFields
                    values={values}
                    touched={touched}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    selectedProducts={selectedProducts}
                  />
                )}


                {selectedProducts["fictitiousNameRenewal"] && (
                  <FictitiousNameRenewalFields
                    values={values}
                    touched={touched}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    selectedProducts={selectedProducts}
                  />
                )}


                {selectedProducts["boiReport"] && (
                  <BOIReportFields
                    values={values}
                    touched={touched}
                    errors={errors}
                    setFieldValue={setFieldValue}
                  />
                )}

                {isGeneralPurchase && (
                  <>
                    <TermsAndConditionsFields
                      values={values}
                      touched={touched}
                      errors={errors}
                      setFieldValue={setFieldValue}
                      selectedProducts={selectedProducts}
                    />

                    <TextInputField
                      fieldName={"electronicSignature"}
                      fieldLabel={"Client Agreement & Signature"}
                      errors={errors}
                      touched={touched}
                      required={true}
                      placeholder={"Electronic Signature"}
                      customClass={"signature-font"}
                    />
                  </>
                )}

                {!isGeneralPurchase && isMultiStep && showTermsAndSignature && (
                  <>
                    <TermsAndConditionsFields
                      values={values}
                      touched={touched}
                      errors={errors}
                      setFieldValue={setFieldValue}
                      selectedProducts={selectedProducts}
                    />

                    <SignatureField
                      errors={errors}
                      touched={touched}
                      values={values}
                      selectedProducts={selectedProducts}
                      setFieldValue={setFieldValue}
                    />

                  </>
                )}

                {!discountId && promoCodes && Object.keys(promoCodes).length > 0 && (
                  <div className="promo-code-container">
                    <div className="promo-code-input">
                      <TextInputField
                        fieldName={"promoCode"}
                        fieldLabel={"Promo Code"}
                        errors={errors}
                        touched={touched}
                        placeholder={"Promo Code"}
                        disabled={isSubmitting || discountId !== ''}
                        extra={{
                          unstyled: true,
                        }}
                        onKeyDown={(e: any) => {
                          // console.log(e)
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handlePromoCode(values.promoCode)
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="step-btn btn promo-btn"
                        disabled={isSubmitting || !values.promoCode}
                        onClick={() => {
                          handlePromoCode(values.promoCode)
                        }}>
                        Check Code
                      </button>
                    </div>
                    {!validDiscount && (
                      <div className="promo-code-error">
                        <span style={{ color: 'red', fontStyle: 'italic' }}>
                          Invalid promo code
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {discountId && (
                  <div className="promo-code-container">
                    <div className="promo-code-applied">
                      <b>Promo Code:</b> {discountId} {discountAmount && discountAmount > 0 ? (
                        <span style={{
                          color: 'red',
                          // italics
                          fontStyle: 'italic'
                        }}>${discountAmount / 100} discount applied</span>
                      ) : null}
                    </div>
                  </div>
                )}

                <div className="final-purchase-form-section">
                  <div className="form-btn-container">

                    {isLastStep && (
                      <button
                        type="submit"
                        className={`submit-btn btn ${disableForm ||
                          isSubmitting ||
                          Object.keys(errors).length > 0
                          ? "disabled-btn"
                          : ""
                          }`}
                        disabled={
                          disableForm ||
                          isSubmitting ||
                          Object.keys(errors).length > 0
                        }
                      >
                        Proceed to Checkout
                      </button>
                    )}

                    {isMultiStep && !isLastStep && (
                      <button
                        type="button"
                        className="step-btn btn"
                        onClick={() => {
                          // if next step text is EIN and current step is annualReport, set einTaxInfo.about.legalName to businessName
                          if (nextStepText === "EIN" && currentProductKey === "annualReport") {
                            setFieldValue("einTaxInfo.about.legalName", values.businessName);
                          }
                          setCurrentStep(currentStep + 1)
                        }}
                        disabled={Object.keys(errors).length > 0}
                      >
                        Proceed to {nextStepText}
                      </button>
                    )}

                    {errors && Object.keys(errors).length > 0 && (
                      <div className="field-container error-btn-container">
                        <div
                          className="form-error form-error-btn"
                          onClick={scrollToError}
                        >
                          <b>
                            There are errors with the form. Click here to fix
                          </b>
                          {window.showFormErrors && (
                            <pre>{JSON.stringify(errors, null, 2)}</pre>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Form>
            </div>
          )}
        </Formik>
      </div>
    </>
  );
}
